import React, { useEffect, useState } from "react";
import { useParams, useHistory } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import moment from "moment";
import { FaMoneyBillWave, FaArrowLeft } from "react-icons/fa";
import ActionButton from "../../Common/ActionButton";
import StatusChip, { statusChipType } from "../../Common/StatusChip";
import TransactionDetailModal from "../../../Common/TransactionDetailModal";
import { useSession } from "../../../context/SessionContext";
import { GetGatepassByIdApi } from "../../../Api/Store/Gatepass";
import { fetchGeneralLedgerTransactions } from "../../../Redux/actions/AccountReports/reports";

const GatepassDetailsPage = () => {
  const { id } = useParams();
  const history = useHistory();
  const dispatch = useDispatch();
  const { rolesdata } = useSession();
  const projects = rolesdata?.data?.Projects || [];
  const generalLedger = useSelector((state) => state.generalLedger);

  const [gatepass, setGatepass] = useState(null);
  const [loading, setLoading] = useState(true);
  const [openTransactionModel, setOpenTransactionModel] = useState(false);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const res = await GetGatepassByIdApi(id);
        if (res?.data?.IsSuccess) {
          setGatepass(res.data.Data);
        } else {
          toast.error(res?.data?.Message || "Failed to load gate pass");
        }
      } catch (err) {
        toast.error("Something went wrong");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [id]);

  const projectName =
    projects.find((p) => p.ProjectId === gatepass?.ProjectId)?.ProjectName || gatepass?.ProjectId;

  const viewTransactions = () => {
    // null = no project filter: pulls every project this Gatepass touched (issuing project's
    // WIP/Payable leg plus, on a cross-project draw, the funding project's Receivable/Inventory
    // leg too), not just whichever project happens to be globally active.
    dispatch(fetchGeneralLedgerTransactions(id, "Gatepass", null));
    setOpenTransactionModel(true);
  };

  if (loading) {
    return <div className="p-4">Loading...</div>;
  }

  if (!gatepass) {
    return <div className="p-4">Gate pass not found.</div>;
  }

  return (
    <div>
      <div className="row">
        <div className="col-md-12 text-left page_heading d-flex justify-content-between align-items-center">
          <div className="d-flex align-items-center">
            <FaArrowLeft
              size={16}
              style={{ cursor: "pointer", marginRight: "12px" }}
              title="Back"
              onClick={() => history.goBack()}
            />
            <h4 className="m-0">
              Gate Pass Details {gatepass.GatepassNumber ? `— ${gatepass.GatepassNumber}` : ""}
            </h4>
          </div>
          <div>
            <ActionButton
              text="View Transactions"
              icon={FaMoneyBillWave}
              variant="save"
              onClick={viewTransactions}
            />
          </div>
        </div>
      </div>

      <div className="row mt-3">
        <div className="col-md-3">
          <small className="text-muted d-block">Project</small>
          <div>{projectName}</div>
        </div>
        <div className="col-md-3">
          <small className="text-muted d-block">Gate Pass Date</small>
          <div>
            {gatepass.GatepassDate ? moment(gatepass.GatepassDate).format("MMM DD YYYY") : ""}
          </div>
        </div>
        <div className="col-md-3">
          <small className="text-muted d-block">Status</small>
          <StatusChip status={gatepass.Status} type={statusChipType(gatepass.Status)} />
        </div>
        <div className="col-md-3">
          <small className="text-muted d-block">Created At</small>
          <div>
            {gatepass.CreatedAt ? moment(gatepass.CreatedAt).format("MMM DD YYYY hh:mm A") : ""}
          </div>
        </div>
      </div>

      {gatepass.MrNumber && (
        <div className="row mt-2">
          <div className="col-md-3">
            <small className="text-muted d-block">Material Requisition</small>
            <div>{gatepass.MrNumber}</div>
          </div>
        </div>
      )}

      {(gatepass.VehicleNumber || gatepass.VehicleNo || gatepass.DriverName) && (
        <div className="row mt-2">
          {(gatepass.VehicleNumber || gatepass.VehicleNo) && (
            <div className="col-md-3">
              <small className="text-muted d-block">Vehicle Number</small>
              <div>{gatepass.VehicleNumber || gatepass.VehicleNo}</div>
            </div>
          )}
          {gatepass.DriverName && (
            <div className="col-md-3">
              <small className="text-muted d-block">Driver Name</small>
              <div>{gatepass.DriverName}</div>
            </div>
          )}
        </div>
      )}

      <div className="row mt-2">
        <div className="col-md-12">
          <small className="text-muted d-block">Note / Purpose</small>
          <div>{gatepass.Note || "-"}</div>
        </div>
      </div>

      <div className="row mt-4">
        <div className="col-md-12">
          <h5 style={{ color: "#056363" }}>Line Items</h5>
          <table className="table table-bordered table-hover" style={{ fontSize: "13px" }}>
            <thead style={{ backgroundColor: "#E8FBFB" }}>
              <tr>
                <th>Sr</th>
                <th>Material</th>
                <th>Packaging</th>
                <th>Quantity</th>
                <th>Remarks</th>
              </tr>
            </thead>
            <tbody>
              {(gatepass.Lines || []).map((line, idx) => (
                <tr key={line.Id || idx}>
                  <td>{idx + 1}</td>
                  <td>{line.MaterialName}</td>
                  <td>{line.PackagingName || "Base"}</td>
                  <td>{line.Quantity}</td>
                  <td>{line.Remarks}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {openTransactionModel && (
        <TransactionDetailModal
          title="Transaction Details"
          transactions={generalLedger?.transactionData || []}
          onClose={() => setOpenTransactionModel(false)}
          debitTotal={generalLedger?.transactionDebitTotal || 0}
          creditTotal={generalLedger?.transactionCreditTotal || 0}
          isLoading={generalLedger?.loading}
          columns={[
            {
              key: "date",
              label: "Date",
              accessor: (t) => moment(t?.EntryDate?.slice(0, 10)).format("MMM DD YYYY"),
            },
            {
              key: "project",
              label: "Project",
              accessor: (t) => t?.Project?.ProjectName,
            },
            {
              key: "account",
              label: "Account",
              accessor: (t) => t?.Account?.AccountTitle,
            },
            {
              key: "narration",
              label: "Narration",
              accessor: (t) => t?.Narration,
            },
          ]}
        />
      )}
    </div>
  );
};

export default GatepassDetailsPage;
